import { NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { usePermissions } from '../../hooks/usePermissions';
import {
  UsersIcon,
  BuildingOfficeIcon,
  DocumentChartBarIcon,
  KeyIcon,
  ShieldCheckIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';

const navigation = [
  { name: 'Clients', href: '/clients', icon: UsersIcon, roles: ['api', 'support'] },
  { name: 'Management Clients', href: '/management-clients', icon: ShieldCheckIcon, roles: ['support'] },
  { name: 'Certificates', href: '/certificates', icon: KeyIcon, roles: ['support'], superAdminOnly: true },
  { name: 'Organizations', href: '/organizations', icon: BuildingOfficeIcon, roles: ['support'] },
  { name: 'Logs', href: '/logs', icon: DocumentChartBarIcon, roles: ['api', 'support'] },
  { name: 'Help', href: '/help', icon: QuestionMarkCircleIcon, roles: ['api', 'support'] },
  // { name: 'Settings', href: '/settings', icon: Cog6ToothIcon, roles: ['support'] },
];

const SidebarContainer = styled.div`
  display: none;
  
  @media (min-width: 1024px) {
    display: flex;
    position: fixed;
    inset: 0;
    z-index: 50;
    width: 16rem;
    flex-direction: column;
  }
`;

const SidebarContent = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xl};
  overflow-y: auto;
  background-color: ${props => props.theme.colors.primary[600]};
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.xl} ${props => props.theme.spacing.md};
`;

const SidebarHeader = styled.div`
  display: flex;
  height: 4rem;
  flex-shrink: 0;
  align-items: center;
`;

const SidebarTitle = styled.h1`
  font-size: ${props => props.theme.fontSizes.xl};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: white;
`;

const Navigation = styled.nav`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const NavigationList = styled.ul`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${props => props.theme.spacing['2xl']};
  list-style: none;
`;

const NavigationSection = styled.li``;

const NavigationItems = styled.ul`
  margin: 0 -${props => props.theme.spacing.sm};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  list-style: none;
`;

const NavigationItem = styled.li``;

const NavigationLink = styled(NavLink)<{ $isActive?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borders.radius.md};
  padding: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSizes.sm};
  line-height: 1.5;
  font-weight: ${props => props.theme.fontWeights.semibold};
  transition: all 0.2s ease;
  
  ${props => props.$isActive ? `
    background-color: ${props.theme.colors.primary[700]};
    color: white;
  ` : `
    color: ${props.theme.colors.primary[200]};
    
    &:hover {
      color: white;
      background-color: ${props.theme.colors.primary[700]};
    }
  `}
`;

const NavigationIcon = styled.div`
  height: 1.5rem;
  width: 1.5rem;
  flex-shrink: 0;
  
  svg {
    height: 100%;
    width: 100%;
  }
`;

export function Sidebar() {
  const { isSupport, isApiUser, isSuperAdmin } = usePermissions();
  const location = useLocation();

  const hasRole = (roles: string[]) => {
    return roles.includes('api') && isApiUser || roles.includes('support') && isSupport;
  };

  return (
    <SidebarContainer>
      <SidebarContent>
        <SidebarHeader>
          <SidebarTitle>Financial Gateway</SidebarTitle>
        </SidebarHeader>
        <Navigation>
          <NavigationList role="list">
            <NavigationSection>
              <NavigationItems role="list">
                {navigation.map((item) => {
                  if (!hasRole(item.roles)) return null;
                  if (item.superAdminOnly && !isSuperAdmin) return null;

                  return (
                    <NavigationItem key={item.name}>
                      <NavigationLink
                        to={item.href}
                        $isActive={location.pathname === item.href}
                      >
                        <NavigationIcon>
                          <item.icon aria-hidden="true" />
                        </NavigationIcon>
                        {item.name}
                      </NavigationLink>
                    </NavigationItem>
                  );
                })}
              </NavigationItems>
            </NavigationSection>
          </NavigationList>
        </Navigation>
      </SidebarContent>
    </SidebarContainer>
  );
}